import React, { useState, useEffect, useRef } from "react"
import { Icon, Segment, Header, Loader, Card, Transition } from "semantic-ui-react"
import moment, { Moment } from "moment"
import backgroundImagePath from "../../assets/images/illustration__isaac_lefever__wilderness.png"
import parkLogoWhitePath from "../../assets/images/park_logo__white.svg"
import parkLogoGreenPath from "../../assets/images/park_logo__green.svg"
import { CampingStyleProps, CampProps, SiteProps } from "../interfaces"
import DatesCard from "./Dates/DatesCard"
import SitesCard from "./Sites/SitesCard"
import SelectionCard from "./DetailsPayment/SelectionCard"
import { validElectric, validRigType } from "~/types"
import { CAMPING_STYLES, PREREQUISITES, STATUSES } from "~/constants"
import { MapPinIcon } from "@heroicons/react/24/solid"
import CampImagesCarousel from "./ui/CampImagesCarousel"
import { BookerProvider } from "~/contexts/BookerContext"
import api from "../api2"
import useCampTheme from "../hooks/useCampTheme"
import AppBoundary from "./Booker/ErrorBoundaries/AppBoundary"
import CampDecorator from "../decorators/CampDecorator"
import createMarkup from "../utils/createMarkup"
import PrerequisiteProps from "../interfaces/PrerequisiteProps"
import { bySort } from "~/utils/sorts"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { useCampImageUploader } from "~/hooks/useCampImageUploader"
import Notification, { NotificationContainer } from "./ui/Notification"
import { ICampNotificationData } from "~/interfaces/ICampNotificationData"
import { IUTMParams } from "~/interfaces/IUTMParams"
import electricOptions from "./Sites/prerequisites/electricOptions"
import checkIsKidsPrereqPresent from "~/utils/isKidsPrereqPresent"
import useGoogleAnalytics from "~/hooks/useGoogleAnalytics"

const initCampingStyle = (campingStyles: CampingStyleProps[], queryParams: URLSearchParams): CampingStyleProps => {
  const queryCs = queryParams.get("camping_style")

  const queryCampingStyle = campingStyles.find((cs) => cs.is_bookable_online && cs.name === queryCs && cs.sites.length > 0)

  return queryCs && queryCampingStyle
    ? queryCampingStyle
    : campingStyles.some((cs) => cs.sort !== null)
      ? campingStyles.sort(bySort).find((cs) => cs.is_bookable_online)
      : campingStyles.find((cs) => cs.name === CAMPING_STYLES.RV && cs.is_bookable_online) ||
        campingStyles.filter((cs) => cs.is_bookable_online)[0]
}

interface ComponentProps {
  camp: CampProps
  preferred_checkin_times: Record<string, string> // e.g. { campingStyleId: '15:00' }
  preferred_checkout_times: Record<string, string> // e.g. { campingStyleId: '15:00' }
}

const Camp = (props: ComponentProps) => {
  const queryParams = new URLSearchParams(decodeURIComponent(window.location.search))
  const mGroupedReservationId = queryParams.get("grouped_reservation")

  const getQueryParamsValue = () => {
    let params: IUTMParams = {}
    const qStartDate = queryParams.get("start_date")
    const qEndDate = queryParams.get("end_date")

    const getValidInt = (value: string | null) => (value && !isNaN(parseInt(value)) ? parseInt(value) : null)

    const qSiteId = queryParams.get("site_id")

    const qGuestCount = getValidInt(queryParams.get("guests"))
    const qKidsCount = getValidInt(queryParams.get("kids"))
    const qRigLength = getValidInt(queryParams.get("rig_length"))
    const qPets = getValidInt(queryParams.get("pets"))
    const qAmperage = queryParams.get("amperage")

    const campingStyleBySiteId = props.camp.camping_styles
        .filter((style) => style.is_bookable_online)
        .find((cs) => cs.sites.find((site) => site.id === qSiteId))

    const campingStyle =
      campingStyleBySiteId ||
      initCampingStyle(props.camp.camping_styles, queryParams)

    const checkinTime = campingStyle.checkin_time || "12:00"
    const checkoutTime = campingStyle.checkout_time || "12:00"

    const [checkoutHour, checkoutMinute] = checkoutTime.split(":").map(Number)
    const [checkinHour, checkinMinute] = checkinTime.split(":").map(Number)
    
    const momentStartDate = moment.utc(qStartDate)?.hour(checkinHour)?.minute(checkinMinute)
    const momentEndDate = moment.utc(qEndDate)?.hour(checkoutHour)?.minute(checkoutMinute)

    if (qStartDate && momentStartDate.isValid()) {
      params = { ...params, utmStartDate: momentStartDate }
    }

    if (qEndDate && momentEndDate.isValid()) {
      params = { ...params, utmEndDate: momentEndDate }
    }

    if (qSiteId?.length) {
      params = { ...params, siteId: qSiteId }
    }

    params = { ...params, campingStyle }

    const requiredFields = campingStyle?.prerequisites.map((pr) => pr.kind)

    if (!requiredFields.length || !campingStyle) {
      return { ...params, campingStyleBySiteId }
    }

    if (requiredFields.includes(PREREQUISITES.PETS) && typeof qPets === "number" && qPets >= 0) {
      params = { ...params, pets: qPets }
    }
    
    if (requiredFields.includes(PREREQUISITES.PASSENGERS) && typeof qGuestCount === "number" && qGuestCount > 0 ) {
      params = { ...params, guests: qGuestCount }
    }
    
    if (requiredFields.includes(PREREQUISITES.PASSENGERS) && checkIsKidsPrereqPresent(campingStyle) && typeof qKidsCount === "number" && qKidsCount >= 0) {
      params = { ...params, kids: qKidsCount }
    }

    if (params?.guests && params?.kids && campingStyle.guest_capacity && params.guests + params.kids > campingStyle.guest_capacity) {
      params = {
        ...params,
        kids: null,
        guests: null,
      }
    }

    if (requiredFields.includes(PREREQUISITES.MAX_RIG_LENGTH) && typeof qRigLength === "number" && qRigLength > 0) {
      params = { ...params, rigLength: qRigLength }
    }

    const campingStyleSites = campingStyle?.sites.filter(
      (site) => site.camping_style_id == campingStyle.id && site.is_bookable,
    )

    if (
      requiredFields.includes(PREREQUISITES.ELECTRIC) &&
      qAmperage?.length &&
      electricOptions(campingStyleSites || [])
        .filter((option) => option.value)
        .find((amp) => amp.value === (qAmperage as validElectric))
    ) {
      params = { ...params, amperage: qAmperage as validElectric }
    }

    return { ...params, campingStyleBySiteId }
  }

  const { utmStartDate, utmEndDate, siteId, campingStyle, guests, kids, rigLength, amperage, campingStyleBySiteId, pets } = getQueryParamsValue()

  const { campPrimaryColor, campSecondaryColor, campHeaderColor, campFontFamily, campName, campCity, campState } =
  useCampTheme(props.camp)

  const [isUserCampingStyleChange, setIsUserCampingStyleChange] = useState<boolean>(false)
  const [camp, setCamp] = useState<CampProps>(props.camp)
  const [startDate, setStartDate] = useState<Moment>(utmStartDate)
  const [endDate, setEndDate] = useState<Moment>(utmEndDate)  
  const [loading, setLoading] = useState<boolean>(true)
  const [isPaying, setIsPaying] = useState(false)
  const [groupIsNotFound, setGroupIsNotFound] = useState(false)
  const [userRigType, setUserRigType] = useState<validRigType>()
  const [userPets, setUserPets] = useState<number>(pets)
  const [userRigYear, setUserRigYear] = useState<number>()

  const [userRigLength, setUserRigLength] = useState<number>(rigLength)
  const [userAmps, setUserAmps] = useState<validElectric>(amperage)
  const [userPassengers, setUserPassengers] = useState<number>(guests)
  const [userKidCampers, setUserKidCampers] = useState<number>(kids)
  const { imageStates, fetchCampImages } = useCampImageUploader(camp)

  const [userCampingStyle, setUserCampingStyle] = useState<CampingStyleProps>(
    campingStyle || initCampingStyle(props.camp.camping_styles, queryParams)
  )

  const [showDatesCard, setShowDatesCard] = useState<boolean>(true)
  const [showSites, setShowSites] = useState<boolean>(false)
  const [showSelectionCard, setShowSelectionCard] = useState(false)
  const [showPayment, setShowPayment] = useState(!mGroupedReservationId && userCampingStyle?.is_skipping_site_selection)

  const isSkippingSiteSelection = mGroupedReservationId ? false : userCampingStyle?.is_skipping_site_selection

  const [activeSite, setActiveSite] = useState<SiteProps>()

  const [groupedReservationId, setGroupedReservationId] = useState(null)  
  const [groupedPrerequisites, setGroupedPrerequisites] = useState<PrerequisiteProps[] | null>(null)
  const [unavailableDates, setUnavailableDates] = useState<Record<string, string[]>>({})

  const [notificationData, setNotificationData] = useState<ICampNotificationData | null>(null)
  const [siteIdParam, setSiteIdParam] = useState<string>(siteId)
  const { trackForCamp, trackForPark } = useGoogleAnalytics(camp)

  const setCampingStyle = (campingStyle: CampingStyleProps) => {
    setUserCampingStyle(campingStyle)
    resetAllPrereq()
  }

  const checkForGroupId = () => {
    if (mGroupedReservationId) {
      api.GroupedReservations.find(mGroupedReservationId)
        .then((grouped_reservation) => {
          setStartDate(moment(grouped_reservation.from_date))
          setEndDate(moment(grouped_reservation.to_date))

          const filteredGroupedReservations = (grouped_reservation.reservations || []).filter(
            (res) => res.status !== STATUSES.PAID && res.site.kind !== CAMPING_STYLES.TENT,
          )
          const firstReservation = filteredGroupedReservations[0] || grouped_reservation.reservations[0]

          const campingStyleId = firstReservation?.selected_camping_style_id || firstReservation?.site?.camping_style_id
          setGroupedReservationId(grouped_reservation.id)
          setGroupedPrerequisites(
            grouped_reservation.reservations
              .map(
                (reservation) =>
                  props?.camp?.camping_styles?.find((cs) => cs?.id === reservation?.site?.camping_style_id)
                    ?.prerequisites,
              )
              ?.flat()
              ?.filter((kind) => kind),
          )
          setUserCampingStyle(props.camp.camping_styles?.find((cs) => cs.id === campingStyleId))
        })
        .catch((error) => {
          setGroupIsNotFound(true)
          console.error(error)
        })
    }
  }

  useEffect(() => {
    if (campingStyleBySiteId && utmStartDate?.isSame(startDate, "day") && utmEndDate?.isSame(endDate, "day")) {
      setShowSites(true)
      setShowDatesCard(false)
    }
  }, [campingStyleBySiteId])

  useEffect(() => {
    setCamp(props.camp)
    trackForCamp("session_initialized")
    trackForPark("session_initialized", {
      camp: props.camp.name,
    })
    setLoading(false)

    checkForGroupId()

    fetchCampImages("camp-photos")
  }, [])

  useEffect(() => {
    if (groupedReservationId) setShowDatesCard(false)
  }, [groupedReservationId])

  useEffect(() => {
    // Reset active/selected sites and do not show sites or selection cards
    setActiveSite(null)

    if (!showDatesCard) {
      setShowSites(true)
      return
    }

    setShowSites(false)
    setShowSelectionCard(false)
  }, [showDatesCard])

  const onDatesComplete = () => {
    // NOTE: Reset active/selected sites
    setActiveSite(null)

    if (startDate && endDate) {
      setShowSites(true)

      if (isSkippingSiteSelection) {
        if (Number(userCampingStyle.prerequisites?.length) < 1) {
          setShowSelectionCard(true)
        } else {
          setShowSites(true)
        }
      }
    } else {
      setShowSites(false)
    }
  }

  useEffect(() => {
    // NOTE: if skipping site selection, use onDatesComplete to show selection card
    if (isSkippingSiteSelection && Number(userCampingStyle.prerequisites?.length) < 1) return

    setShowSelectionCard(!!activeSite)
  }, [activeSite])

  const resetAllPrereq = () => {
    setUserRigType(null)
    setUserRigLength(null)
    setUserAmps(null)
    setUserPassengers(null)
    setUserKidCampers(null)
    setUserPets(null)
    setUserRigYear(null)
  }

  useEffect(() => {
    setIsUserCampingStyleChange(true)
    if (isUserCampingStyleChange && startDate && !groupedReservationId) {
      setStartDate(null)
      setEndDate(null)
    }
  }, [userCampingStyle])

  const onSubmitError = (errorMessage: string) => {
    // NOTE: trigger new fetching of sites
    const og = userRigLength
    setUserRigLength(null)
    setUserRigLength(og)
  }

  const queryObject: { t?: string } = decodeURIComponent(window.location.search)
    .split("?")
    .pop()
    .split("&")
    .reduce((obj, x) => {
      const [key, value] = x.split("=")
      obj[key] = value
      return obj
    }, {})

  const bypassUnpublishedFlag = queryObject.t == "bypass"

  // TODO: replace all camp references with decoratedCamp via API
  const decoratedCamp = new CampDecorator(camp)

  if (groupIsNotFound) {
    return (
      <>
        <Transition show={true} enter="transition-opacity duration-700" enterFrom="opacity-0" enterTo="opacity-100">
          <div
            id="404-bg-image"
            className="z-0 absolute h-full w-full bg-gray-800"
            style={{
              backgroundImage: `url(${backgroundImagePath})`,
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
            }}
          />
        </Transition>
        <div className="h-full w-full flex justify-center items-center">
          <div className="md:mx-0 mx-4 z-10 max-w-screen-sm box-border mt-32 md:mt-72 bg-white rounded-xl md:p-16 p-8 gray-900 shadow-xl bg-opacity-90">
            <div className="text-5xl font-black mb-8">Oops!</div>
            <p className="text-lg">That link is no longer available.</p>
            <p className="text-lg mt-4">
              You can book a reservation by <a href={window.location.pathname}>clicking here</a>, or contact us for a
              new link.
            </p>
          </div>

          <img src={parkLogoWhitePath} className="z-10 h-16 absolute md:left-16 bottom-16 filter drop-shadow-xl" />
        </div>
      </>
    )
  }

  return (
    <AppBoundary camp={camp}>
      <BookerProvider
        camp={camp}
        preferred_checkin_times={props.preferred_checkin_times}
        preferred_checkout_times={props.preferred_checkout_times}
        campingStyle={userCampingStyle}
        setCampingStyle={setCampingStyle}
        selectedSite={activeSite}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        userElectric={userAmps}
        groupedReservationId={groupedReservationId}
        setGroupedReservationId={setGroupedReservationId}
        secondaryColor={campSecondaryColor}
        unavailableDates={unavailableDates}
        setUnavailableDates={setUnavailableDates}
        userPets={userPets}
        setUserPets={setUserPets}
        userRigYear={userRigYear}
        setUserRigYear={setUserRigYear}
        siteIdParam={siteIdParam}
        setSiteIdParam={setSiteIdParam}
        setNotificationData={setNotificationData}
        isQuerySiteIdValid={!!campingStyleBySiteId}
      >
        {(props.camp.is_published && decoratedCamp.isBookable) || bypassUnpublishedFlag ? (
          loading ? (
            <Loader active />
          ) : (
            <div className="flex-column">
              <BookerHeader camp={props.camp} />

              {/* NOTE: only show bio if dates card is visible + camp has bio */}
              <Transition animation="fade right" duration={300} visible={showDatesCard && !!props.camp.bio}>
                {/* TODO: add check if there are images but no bio */}
                <Segment basic>
                  <div
                    id="camp-bio"
                    className="html-editor-container"
                    dangerouslySetInnerHTML={createMarkup(props.camp.bio)}
                  />
                </Segment>
              </Transition>

              {imageStates.map((image) => image.url).length > 0 && showDatesCard && (
                <div className="w-full">
                  <CampImagesCarousel
                    imageUrls={imageStates.map((image) => image.url)}
                    isLoading={imageStates.some((image) => image.isLoading)}
                  />
                </div>
              )}

              <DatesCard
                visible={showDatesCard}
                setShowDatesCard={setShowDatesCard}
                onComplete={onDatesComplete}
                resetAllPrereq={resetAllPrereq}
              />
              <SitesCard
                visible={showSites}
                camp={camp}
                startDate={startDate}
                endDate={endDate}
                setShowDatesCard={setShowDatesCard}
                isShowingDatesCard={showDatesCard}
                setActiveSite={setActiveSite}
                userRigType={userRigType}
                setUserRigType={setUserRigType}
                userRigLength={userRigLength}
                setUserRigLength={setUserRigLength}
                userAmps={userAmps}
                setUserAmps={setUserAmps}
                userPassengers={userPassengers}
                setUserPassengers={setUserPassengers}
                userKidCampers={userKidCampers}
                setUserKidCampers={setUserKidCampers}
                userCampingStyle={userCampingStyle}
                showPayment={showPayment}
                groupedPrerequisites={groupedPrerequisites}
                groupId={mGroupedReservationId}
                isSkippingSiteSelection={isSkippingSiteSelection}
                setShowPayment={setShowPayment}
                userPets={userPets}
              />
              <SelectionCard
                visible={showSelectionCard}
                camp={camp}
                site={activeSite}
                startDate={startDate}
                endDate={endDate}
                setIsPaying={setIsPaying}
                userRigType={userRigType}
                userRigLength={userRigLength}
                userElectric={userAmps}
                userPassengers={userPassengers}
                userKidCampers={userKidCampers}
                userCampingStyle={userCampingStyle}
                onError={onSubmitError}
                skipShowFeatures={isSkippingSiteSelection}
                showPayment={showPayment}
                setShowPayment={setShowPayment}
              />
            </div>
          )
        ) : (
          <div className="flex-column camp-header">
            <div
              className="flex justify-center h-30 shadow-none drop-shadow-none"
              style={{ backgroundColor: camp.primary_color }}
            >
              <div className="ml-4 flex-grow flex items-center my-8">
                <div className="text-white" id="camp-text" style={{ color: camp.header_color }}>
                  <h2 className="text-2xl sm:text-3xl header pt-0.5">{campName}</h2>
                  <span className="subheader flex items-center">
                    <MapPinIcon className="w-5 h-5 mr-1.5" />
                    <span>{`${campCity}, ${campState}`}</span>
                  </span>
                </div>
                {props.camp.logo_url && (
                  <img src={props.camp.logo_url} className="h-32 ml-auto py-2 mr-4 " style={{ maxWidth: "50%" }} />
                )}
              </div>
            </div>
            <Card fluid id="coming-soon-segment">
              <div className="content">
                <Segment placeholder style={{ textAlign: "center" }}>
                  <Header icon>
                    <Icon name="map" />
                    {decoratedCamp.bookingStartDatetime
                      ? "Reservations for next season will open soon!"
                      : "Coming Soon!"}
                  </Header>
                  <div className="flex justify-center">
                    {props.camp.is_published ? (
                      decoratedCamp.bookingStartDatetime ? (
                        <div className="text-baselg max-w-2xl">
                          Reservations for the upcoming season will be available for booking starting on{" "}
                          <span className="font-bold">
                            {decoratedCamp.bookingStartDatetime.toFormat("LLLL d")} at{" "}
                            {decoratedCamp.bookingStartDatetime.toFormat("h:mm a ZZZZ")}
                          </span>
                          . We look forward to welcoming you soon!
                        </div>
                      ) : (
                        <div className="text-baselg max-w-2xl">
                          Reservations for the upcoming season will be available soon, we look forward to welcoming you.
                        </div>
                      )
                    ) : (
                      <div className="text-baselg max-w-2xl">
                        We're excited to go live with our online bookings soon, in the meantime please{" "}
                        {props.camp.phone && (
                          <>
                            call our office at
                            <a href={`tel:${props.camp.phone}`} className="mx-1 font-bold">
                              {props.camp.phone}
                            </a>
                          </>
                        )}
                        {props.camp.phone && props.camp.email && "or "}
                        {props.camp.email && (
                          <>
                            email us at
                            <a href={`mailto:${props.camp.email}`} className="mx-1 font-bold">
                              {props.camp.email}
                            </a>
                          </>
                        )}
                        to make a reservation.
                      </div>
                    )}
                  </div>
                </Segment>
              </div>
            </Card>
          </div>
        )}
        <NotificationContainer>
          <Notification
            visible={!!notificationData}
            error={notificationData?.isError}
            title={notificationData?.title}
            description={notificationData?.description}
            onDismiss={() => setNotificationData(null)}
            dismissAfter={10000}
          />
        </NotificationContainer>
      </BookerProvider>
      {showDatesCard && !showSites && !showSelectionCard && (
        <>
          <a
            href="https://parkwith.us?utm_source=booker_badge"
            target="_blank"
            rel="noopener noreferrer"
            className="flex sm:hidden fixed bottom-3.5 left-4 z-[999] flex-col items-center px-3.5 pt-1 pb-1.5  bg-white bg-opacity-90 rounded-md shadow-md  text-[#416149] hover:text-[#416149] hover:bg-gray-50 transition-colors border border-gray-75"
          >
            <span className="font-medium text-gray-450 text-xs tracking-tight">Powered by</span>
            <img src={parkLogoGreenPath} alt="Park Logo" className="h-4.5" />
          </a>
          <a
            href="https://parkwith.us?utm_source=booker_badge"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex fixed items-center px-2.5 py-1.5 bottom-4 left-4 z-[999] bg-white bg-opacity-90 rounded-md shadow-md  text-[#416149] hover:text-[#416149] hover:bg-gray-50 transition-colors border border-gray-75"
          >
            <span className="font-medium text-gray-450 text-md tracking-tight">Powered by</span>
            <img src={parkLogoGreenPath} alt="Park Logo" className="h-5 ml-2" />
          </a>
        </>
      )}
    </AppBoundary>
  )
}

export const BookerHeader = ({ camp }: { camp: CampProps }) => {
  const { campPrimaryColor, campFontFamily, campCity, campState } = useCampTheme(camp)

  return (
    <>
      {!camp.stripe_account_id && (
        <div className="sticky top-0 z-30 w-full flex items-center justify-center bg-yellow-100 text-yellow-600 p-1 border-b border-yellow-300">
          <ExclamationTriangleIcon className="w-6 h-6 m-4 flex-shrink-0" />
          <p className="text-lg font-bold">
            We're not accepting online reservations at the moment. Please{" "}
            {camp.phone && (
              <>
                call our office at
                <a href={`tel:${camp.phone}`} className="mx-1 font-bold">
                  {camp.phone}
                </a>
              </>
            )}
            {camp.phone && camp.email && "or "}
            {camp.email && (
              <>
                email us at
                <a href={`mailto:${camp.email}`} className="mx-1 font-bold">
                  {camp.email}
                </a>
              </>
            )}
            for more information.
          </p>
        </div>
      )}
      <div
        className="flex justify-center h-30 shadow-none drop-shadow-none"
        style={{ backgroundColor: campPrimaryColor }}
      >
        <div className="ml-4 flex-grow flex items-center my-8">
          <div id="camp-text" style={{ color: camp.header_color }}>
            <h1
              className="text-2xl sm:text-3xl header pt-0.5 font-semibold"
              style={{ fontFamily: campFontFamily || "Lora" }}
            >
              {camp.name}
            </h1>
            <span className="flex items-center mb-1.5 mt-1">
              <MapPinIcon className="w-5 h-5 mr-1.5" />
              <h2>{campCity && !campState ? `${campCity}` : `${campCity}, ${campState}`}</h2>
            </span>
          </div>
          {camp.logo_url && <img src={camp.logo_url} className="h-32 ml-auto py-2 mr-4" style={{ maxWidth: "50%" }} />}
        </div>
      </div>
    </>
  )
}

export default Camp
